import { Link as RouterLink, useHistory } from "react-router-dom";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Login, sendCodeVerificationCode } from "../../redux/actions/userActions";
import {
  ORGANIZATION_DETAILS_SUCCESS,
  ORGANIZATION_LIST_SUCCESS,
} from "../../redux/constants/OrganizationConstants";

import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import AuthShell from "../auth/AuthShell";
import Spinner from "../common/Spinner";

const LoginPage = () => {
  const history = useHistory();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useSelector((state) => state.userLogin); // keep the subscription for auth redirects

  const [formState, setFormState] = useState({ values: {} });

  const handleChange = (event) => {
    setFormState((fs) => ({
      ...fs,
      values: { ...fs.values, [event.target.name]: event.target.value },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setError("");
    const { email, password } = formState.values;
    if (!email || !password) return;

    setSubmitting(true);
    dispatch(Login(email, password)).then((response) => {
      setSubmitting(false);
      if (response?.message === "Invalid Email or Password") {
        setError("Invalid Email or Password. Please try again.");
      } else if (response?.message === "User Not Verified.") {
        setLoading(true);
        dispatch(sendCodeVerificationCode(email)).then((res) => {
          setLoading(false);
          if (res?.success === 1) history.push(`/Verify/${res?.data?.token}`);
        });
      } else if (
        response?.data?.role !== "Super_Admin" &&
        response?.data?.organizations?.length === 0
      ) {
        history.push("/organization/create");
      } else if (response?.data?.role === "Super_Admin") {
        history.push("/organizations");
      } else {
        dispatch({
          type: ORGANIZATION_LIST_SUCCESS,
          payload: { data: response?.data?.organizations },
        });
        dispatch({
          type: ORGANIZATION_DETAILS_SUCCESS,
          payload: response?.data?.organizations[0],
        });
        history.push("/apps");
      }
    });
  };

  return (
    <>
      <AuthShell
        title="Welcome back"
        subtitle="Sign in to continue to Beta Appstore."
        footer={
          <Typography variant="body2" align="center" sx={{ color: "text.secondary" }}>
            Don&apos;t have an account?{" "}
            <Link component={RouterLink} to="/register" sx={{ fontWeight: 700 }}>
              Create one
            </Link>
          </Typography>
        }
      >
        <form onSubmit={handleSubmit} autoComplete="off" noValidate>
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Email"
              type="email"
              name="email"
              required
              fullWidth
              autoFocus
              autoComplete="email"
              value={formState.values.email || ""}
              onChange={handleChange}
              error={submitted && !formState.values.email}
              helperText={submitted && !formState.values.email ? "Please fill out this field." : " "}
            />

            <TextField
              label="Password"
              type={passwordVisible ? "text" : "password"}
              name="password"
              required
              fullWidth
              autoComplete="current-password"
              value={formState.values.password || ""}
              onChange={handleChange}
              error={submitted && !formState.values.password}
              helperText={submitted && !formState.values.password ? "Please fill out this field." : " "}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setPasswordVisible((v) => !v)}
                      edge="end"
                      aria-label={passwordVisible ? "Hide password" : "Show password"}
                    >
                      {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </Button>

            <Stack direction="row" justifyContent="flex-end">
              <Link component={RouterLink} to="/forgot-password" sx={{ fontSize: 14, fontWeight: 600 }}>
                Forgot password?
              </Link>
            </Stack>
          </Stack>
        </form>
      </AuthShell>

      {loading && <Spinner fullscreen label="Sending verification code…" />}
    </>
  );
};

export default LoginPage;
